export const CONSENT_STORAGE_KEY = "perspective_cookie_consent";
export const CONSENT_VERSION = 2;
export const GOOGLE_TAG_ID = "GT-MRLWPFLT";
export const GOOGLE_ADS_TAG_ID = "AW-18485671480";
export const GA_MEASUREMENT_ID = "G-JT3GC1774V";

export type ConsentPreference = {
  version: number;
  analytics: boolean;
};

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    [key: `ga-disable-${string}`]: boolean;
  }
}

const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID?.trim() || GA_MEASUREMENT_ID;
let measurementLoaded = false;

export function setDefaultConsent() {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  };
  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });
}

export function readConsent(): ConsentPreference | null {
  try {
    const value = JSON.parse(localStorage.getItem(CONSENT_STORAGE_KEY) || "null");
    return value?.version === CONSENT_VERSION && typeof value.analytics === "boolean"
      ? value
      : null;
  } catch {
    return null;
  }
}

export function saveConsent(analytics: boolean): ConsentPreference {
  const preference = { version: CONSENT_VERSION, analytics };
  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(preference));
  applyConsent(analytics);
  return preference;
}

export function applyConsent(analytics: boolean) {
  window.gtag("consent", "update", {
    analytics_storage: analytics ? "granted" : "denied",
    ad_storage: analytics ? "granted" : "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });

  if (measurementId) {
    window[`ga-disable-${measurementId}`] = !analytics;
  }
  if (analytics) initializeMeasurement();
}

function initializeMeasurement() {
  if (measurementLoaded) return;
  measurementLoaded = true;

  const loaderId = GOOGLE_TAG_ID;
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(loaderId)}`;
  script.dataset.perspectiveMeasurement = "true";
  document.head.appendChild(script);

  window.gtag("js", new Date());

  if (measurementId) {
    window.gtag("config", measurementId, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });
  }

  window.gtag("config", GOOGLE_ADS_TAG_ID, {
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
}

export function trackPageView(pathname: string) {
  if (!measurementId || !readConsent()?.analytics || !measurementLoaded) return;
  window.gtag("event", "page_view", {
    send_to: measurementId,
    page_location: `${window.location.origin}${pathname}`,
    page_path: pathname,
    page_title: document.title,
  });
}
